import { useEffect, useRef, useState } from 'react';
import type { DragEvent, JSX, MouseEvent, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Clock, Columns2, Maximize2, Minimize2, PanelRight, Pin, Play, X } from 'lucide-react';
import { IconButton } from '../ui/icon-button';
import { PANE_HEAD } from './PaneParts.js';
import type { TranslationKeys } from '../../i18n/types.js';
import type { StudioEntityRef } from './StudioRail.js';

export type PaneLevel = 'pane' | 'full';

/**
 * Who a workspace object is — as the tabs, the second pane's header and the
 * full-view strip show it (GM Studio 5.4). The type is an icon AND a short
 * visible word; colour is never the only sign.
 */
export interface EntityIdentity {
  readonly label: string;
  /** «Карта», «Лист NPC», «Запись: Локация». */
  readonly typeLabel: string;
  readonly icon: LucideIcon;
  /** Only when needed: tells same-named open objects apart. */
  readonly context?: string;
  /** A map the players see — the confirmed activePageId, never a guess. */
  readonly playersMap?: boolean;
}

export interface WorkspaceChip {
  readonly ref: StudioEntityRef;
  readonly label: string;
  /** Has an unsaved draft (amber dot on the tab). */
  readonly draft?: boolean;
  /** Open tab (manual / opened from the Library) — closable via the close button. */
  readonly removable?: boolean;
  /** Preview tab (italic, replaceable) — at most one across the whole tab bar. */
  readonly preview?: boolean;
  /** Muted suggested chip (its ref is already an open tab). */
  readonly muted?: boolean;
  /** Type, context and the players' mark (tabs). */
  readonly identity?: EntityIdentity;
}

/**
 * The second pane — ONE field for what it shows and who holds it, so a pinned
 * object and the temporary inspector never compete for it.
 */
export interface SidePane {
  readonly ref: StudioEntityRef;
  /** true — kept by the GM (⧉, «Pin», «Open beside»); false — the temporary inspector opened from a map. */
  readonly held: boolean;
}

export interface StudioWorkspaceProps {
  /** Working-set tabs (extraRefs of the selected beat) — may be empty. */
  readonly tabs: readonly WorkspaceChip[];
  readonly focusRef: StudioEntityRef | undefined;
  /** The second pane (undefined — none). */
  readonly side?: SidePane | undefined;
  /** The single preview tab (session-only). */
  readonly previewRef?: StudioEntityRef | undefined;
  readonly onFocus: (ref: StudioEntityRef) => void;
  /** Hold `ref` in the second pane; undefined = empty the second pane (unpin / close the inspector). */
  readonly onPin: (ref: StudioEntityRef | undefined) => void;
  /**
   * Render an entity at pane/full level. All context wiring (sheets,
   * journal, pages, WS sending) lives in the parent (StudioSurface) — the
   * workspace only handles layout and interactions (focus, pin, in-place ⤢
   * escalation). Returning null = entity unavailable.
   */
  readonly renderEntity: (
    ref: StudioEntityRef,
    level: PaneLevel,
    exitFull: () => void,
  ) => JSX.Element | null;
  /** Identity of an object for the second pane's header and the full-view strip. */
  readonly describe?: (ref: StudioEntityRef) => EntityIdentity | undefined;
  /** Full view without a second object: «Open beside» (the parent lets the GM choose one). */
  readonly onOpenBeside?: () => void;
  /**
   * Optional tab wrapper (hover-peek): the parent may wrap a tab in a
   * PeekCard for sheets. Without the prop the tab renders as-is.
   */
  readonly wrapChip?: (ref: StudioEntityRef, chip: JSX.Element) => JSX.Element;
  /** Focus-pane content when no entity is selected (beat panel, etc.). */
  readonly emptyPane?: JSX.Element;
  /** «N замечаний — открыть» (group E): the line under the title of what is open (ref undefined — the beat overview). */
  readonly remarksBadge?: (ref: StudioEntityRef | undefined) => JSX.Element | null;
  /**
   * The selected beat while a material is open in the main pane: its name and
   * «Обзор бита», which brings the beat panel back without closing tabs.
   */
  readonly beatContext?: { readonly label: string; readonly onOverview: () => void };
  /** "＋" on the tab bar — manual entity add. */
  readonly onAddChip?: () => void;
  /** "✕" on a removable tab — close the tab. */
  readonly onRemoveChip?: (ref: StudioEntityRef) => void;
  /** Drag-reorder tabs (from → to). */
  readonly onReorder?: (fromRef: StudioEntityRef, toRef: StudioEntityRef) => void;
  /** Promote the preview tab to a permanent tab (double-click on the preview tab). */
  readonly onPromotePreview?: () => void;
  /** P09b: a bar over the object beside (the map bar of the beat: «Map [select] · this beat's map»). */
  readonly sideBar?: (ref: StudioEntityRef) => ReactNode;
  readonly t: (key: keyof TranslationKeys) => string;
}

const sameRef = (a: StudioEntityRef | undefined, b: StudioEntityRef | undefined): boolean =>
  a !== undefined && b !== undefined && a.kind === b.kind && a.id === b.id;

const refKey = (ref: StudioEntityRef | undefined): string => (ref === undefined ? '' : `${ref.kind}:${ref.id}`);

export function StudioWorkspace({
  tabs,
  focusRef,
  side,
  previewRef,
  onFocus,
  onPin,
  renderEntity,
  describe,
  onOpenBeside,
  wrapChip,
  emptyPane,
  remarksBadge,
  beatContext,
  onAddChip,
  onRemoveChip,
  onReorder,
  onPromotePreview,
  sideBar,
  t,
}: StudioWorkspaceProps): JSX.Element {
  // In-place "⤢ full" escalation — not a modal (epic principle P5). Lives
  // locally: changing focus falls back to pane level.
  const [fullRef, setFullRef] = useState<StudioEntityRef | undefined>(undefined);
  // The tab currently being dragged for reorder (local, session-only).
  const [dragRef, setDragRef] = useState<StudioEntityRef | undefined>(undefined);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const selectedTabKey = refKey(focusRef);
  useEffect((): void => {
    const bar = tabBarRef.current;
    const selected = bar?.querySelector<HTMLElement>('[aria-current="true"]');
    if (bar === null || selected === undefined || selected === null) return;
    // Reveal the selected tab inside its row without scrolling the Studio workspace.
    const rowBounds = bar.getBoundingClientRect();
    const tabBounds = (selected.closest('[data-tab-state]') ?? selected).getBoundingClientRect();
    if (tabBounds.left < rowBounds.left) bar.scrollLeft -= rowBounds.left - tabBounds.left;
    else if (tabBounds.right > rowBounds.right) bar.scrollLeft += tabBounds.right - rowBounds.right;
  }, [selectedTabKey, tabs.length]);
  const focusLevel: PaneLevel = sameRef(fullRef, focusRef) ? 'full' : 'pane';
  // The beat overview replaces the document: its full mode ends with it, so
  // returning to the document (tab) never lands in a stale full screen.
  useEffect((): void => {
    if (focusRef === undefined) setFullRef(undefined);
  }, [focusRef]);

  // The second pane shows another object than the main one (the same object
  // is never shown twice).
  const beside: SidePane | undefined = side !== undefined && !sameRef(side.ref, focusRef) ? side : undefined;
  // A NEW object in the second pane (opened from a map, pinned, «Open
  // beside») is meant to be seen: it ends the full view.
  const sideKey = refKey(side?.ref);
  const lastSideKey = useRef(sideKey);
  useEffect((): void => {
    if (sideKey !== lastSideKey.current && sideKey !== '') setFullRef(undefined);
    lastSideKey.current = sideKey;
  }, [sideKey]);

  // Expanding the focus pane (⤢) hides the second pane so the focus gets the
  // whole workspace width; a narrow strip names what is hidden and brings it
  // back. A hidden map pane unmounts and hands its content back; on return it
  // reloads with its pan/zoom (StudioMapPanes).
  const showSide = beside !== undefined && focusLevel !== 'full';
  const sideRef = useRef<HTMLElement>(null);
  const focusSideNext = useRef(false);
  useEffect((): void => {
    if (showSide && focusSideNext.current) {
      focusSideNext.current = false;
      sideRef.current?.focus();
    }
  }, [showSide]);
  const restoreTwoPanes = (): void => {
    focusSideNext.current = true;
    setFullRef(undefined);
  };

  const named = (identity: EntityIdentity | undefined, fallback: string): string =>
    identity === undefined
      ? fallback
      : t('studio.ref.named').replace('{type}', identity.typeLabel).replace('{name}', identity.label);
  const besideIdentity = beside !== undefined ? describe?.(beside.ref) : undefined;
  const BesideIcon = besideIdentity?.icon;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2" data-testid="studio-workspace">
      {/* The prototype has no tab bar: it appears only while something is open (a held map or a record). */}
      {tabs.length === 0 ? null : (
      <div ref={tabBarRef} className="studio-tab-bar flex flex-none items-center gap-1.5" data-testid="studio-tab-bar">
        {(
          tabs.map((chip) => {
            const active = sameRef(chip.ref, focusRef);
            const pinned = side?.held === true && sameRef(chip.ref, side.ref);
            const isBeside = sameRef(chip.ref, beside?.ref);
            const isPreview = chip.preview === true || sameRef(chip.ref, previewRef);
            const dragging = sameRef(dragRef, chip.ref);
            const identity = chip.identity;
            const TypeIcon = identity?.icon;
            // What a sighted GM reads from icons and type, said in words too.
            const states = [
              active ? t('studio.tabs.selected') : '',
              isBeside ? t('studio.tabs.beside') : '',
              isPreview ? t('studio.tabs.temporary') : '',
              identity?.playersMap === true ? t('studio.tabs.playersMap') : '',
            ].filter((s) => s !== '');
            const description = [
              named(identity, chip.label),
              identity?.context ?? '',
              ...states,
            ].filter((s) => s !== '').join(' · ');
            const chipEl = (
              <span
                className={`inline-flex h-7 items-center gap-1 rounded-md border px-2 text-meta ${
                  active
                    ? 'border-hl/60 bg-hl/10 font-semibold text-fg'
                    : 'border-line bg-panel text-fg'
                }${dragging ? ' opacity-40' : ''}`}
                data-tab-state={[active ? 'selected' : '', isBeside ? 'beside' : '', isPreview ? 'temporary' : ''].filter((s) => s !== '').join(' ') || undefined}
                draggable={onReorder !== undefined}
                onDragStart={(): void => setDragRef(chip.ref)}
                onDragOver={(e: DragEvent<HTMLSpanElement>): void => {
                  if (onReorder !== undefined) {
                    e.preventDefault();
                  }
                }}
                onDrop={(e: DragEvent<HTMLSpanElement>): void => {
                  e.preventDefault();
                  if (
                    onReorder !== undefined &&
                    dragRef !== undefined &&
                    !sameRef(dragRef, chip.ref)
                  ) {
                    onReorder(dragRef, chip.ref);
                  }
                  setDragRef(undefined);
                }}
                onDragEnd={(): void => setDragRef(undefined)}
                onAuxClick={(e: MouseEvent<HTMLSpanElement>): void => {
                  if (e.button === 1 && chip.removable === true && onRemoveChip !== undefined) {
                    e.preventDefault();
                    onRemoveChip(chip.ref);
                  }
                }}
              >
                <button
                  type="button"
                  className={`inline-flex min-w-0 cursor-pointer items-center gap-1${isPreview ? ' italic' : ''}`}
                  data-testid={`tab-${chip.ref.kind}-${chip.ref.id}`}
                  aria-current={active ? 'true' : undefined}
                  aria-label={description}
                  title={description}
                  onClick={(): void => {
                    setFullRef(undefined);
                    onFocus(chip.ref);
                  }}
                  onDoubleClick={(): void => {
                    if (isPreview && onPromotePreview !== undefined) {
                      onPromotePreview();
                    }
                  }}
                >
                  {chip.draft ? <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-hl" /> : null}
                  {TypeIcon !== undefined ? <TypeIcon size={12} className="flex-none text-muted-foreground" aria-hidden="true" /> : null}
                  {identity !== undefined ? (
                    <span className="text-label font-semibold uppercase tracking-label text-dim flex-none" data-tab-type>
                      {identity.typeLabel}
                    </span>
                  ) : null}
                  <span className="truncate" data-tab-name>{chip.label}</span>
                  {identity?.context !== undefined ? (
                    <span className="truncate font-normal text-dim" data-tab-context>
                      {identity.context}
                    </span>
                  ) : null}
                  {identity?.playersMap === true ? (
                    <Play size={11} className="flex-none text-ok" aria-hidden="true" data-tab-mark="players" />
                  ) : null}
                  {isBeside ? (
                    <PanelRight size={12} className="flex-none text-ai" aria-hidden="true" data-tab-mark="beside" />
                  ) : null}
                  {isPreview ? (
                    <Clock size={11} className="flex-none text-dim" aria-hidden="true" data-tab-mark="temporary" />
                  ) : null}
                </button>
                <button
                  type="button"
                  className={`inline-flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${pinned ? 'text-hl' : 'text-dim hover:text-fg'}`}
                  title={t(pinned ? 'studio.workingSet.unpin' : 'studio.workingSet.pin')}
                  aria-label={t(pinned ? 'studio.workingSet.unpin' : 'studio.workingSet.pin')}
                  aria-pressed={pinned}
                  data-testid={`pin-${chip.ref.kind}-${chip.ref.id}`}
                  onClick={(): void => onPin(pinned ? undefined : chip.ref)}
                >
                  <Columns2 size={12} aria-hidden="true" />
                </button>
                {chip.removable === true && onRemoveChip !== undefined ? (
                  <button
                    type="button"
                    className="inline-flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-sm text-dim hover:text-danger focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    title={t('studio.tabs.close')}
                    aria-label={t('studio.tabs.close')}
                    data-testid={`close-${chip.ref.kind}-${chip.ref.id}`}
                    onClick={(): void => onRemoveChip(chip.ref)}
                  >
                    <X size={12} aria-hidden="true" />
                  </button>
                ) : null}
              </span>
            );
            const key = `${chip.ref.kind}:${chip.ref.id}`;
            const wrapped = wrapChip?.(chip.ref, chipEl) ?? chipEl;
            return <span key={key} className="inline-flex">{wrapped}</span>;
          })
        )}
        {onAddChip !== undefined ? (
          <button
            type="button"
            className="inline-flex items-center rounded-full border border-line bg-panel px-2.5 py-0.5 text-meta text-dim hover:border-hl/50 hover:text-fg"
            data-testid="workspace-add-chip"
            title={t('studio.workingSet.add')}
            onClick={onAddChip}
          >
            {'＋'}
          </button>
        ) : null}
      </div>
      )}

      <div className="flex min-h-0 flex-1 gap-2">
        {/* A pane keeps its header; its body scrolls (a document inside its own
            pane), a map fills the body (StudioMapCanvas). */}
        <section
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-line bg-panel"
          data-testid="focus-pane"
        >
          {focusRef !== undefined ? (
            <>
              {/* The pane's header (group T): 40 px — the beat it belongs to, «Обзор бита», ⤢ as an icon. */}
              <div className={`${PANE_HEAD} justify-end px-2`} data-pane-head="">
                {beatContext !== undefined ? (
                  <div className="mr-auto flex min-w-0 items-center gap-2 text-meta" data-testid="beat-context">
                    <span className="truncate text-muted-foreground">
                      {t('studio.beat.one')}: <span className="text-fg">{beatContext.label}</span>
                    </span>
                    <button
                      type="button"
                      className="flex-none rounded border border-hl/40 bg-hl/10 px-2 py-0.5 text-hl hover:bg-hl/20"
                      data-testid="beat-overview"
                      onClick={beatContext.onOverview}
                    >
                      {t('studio.beat.overview')}
                    </button>
                  </div>
                ) : null}
                <IconButton
                  label={t(focusLevel === 'full' ? 'studio.full.exit' : 'studio.entity.openFull')}
                  icon={focusLevel === 'full' ? <Minimize2 size={14} aria-hidden="true" /> : <Maximize2 size={14} aria-hidden="true" />}
                  aria-pressed={focusLevel === 'full'}
                  data-testid="escalate-focus"
                  onClick={(): void =>
                    setFullRef(focusLevel === 'full' ? undefined : focusRef)
                  }
                />
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="focus-pane-body">
                {remarksBadge?.(focusRef) ?? null}
                {renderEntity(focusRef, focusLevel, (): void => setFullRef(undefined))}
              </div>
            </>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="focus-pane-body">
              {emptyPane !== undefined ? (
                <>
                  {remarksBadge?.(undefined) ?? null}
                  {emptyPane}
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center text-ui text-dim">
                  {t('studio.workspace.placeholder')}
                </div>
              )}
            </div>
          )}
        </section>

        {showSide && beside !== undefined ? (
          <section
            ref={sideRef}
            tabIndex={-1}
            // «Map beside» of a beat: the map stands to the LEFT of the beat, as in the prototype (quests__split).
            className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-line bg-panel outline-none focus-visible:ring-2 focus-visible:ring-hl/60${focusRef === undefined && beside.ref.kind === 'page' ? ' order-first' : ''}`}
            data-testid="pinned-pane"
            data-side-state={beside.held ? 'pinned' : 'temporary'}
            aria-label={t('studio.side.region').replace('{name}', named(besideIdentity, beside.ref.id))}
          >
            <div className={`${PANE_HEAD} gap-1.5 px-2 text-meta`} data-testid="side-pane-header" data-pane-head="">
              {BesideIcon !== undefined ? <BesideIcon size={13} className="flex-none text-muted-foreground" aria-hidden="true" /> : null}
              {besideIdentity !== undefined ? (
                <span className="text-label font-semibold uppercase tracking-label text-dim flex-none">{besideIdentity.typeLabel}</span>
              ) : null}
              <span
                className="min-w-0 truncate font-medium text-fg"
                data-testid="side-pane-name"
                title={named(besideIdentity, beside.ref.id)}
              >
                {besideIdentity?.label ?? ''}
              </span>
              {besideIdentity?.context !== undefined ? (
                <span className="min-w-0 truncate text-dim">{besideIdentity.context}</span>
              ) : null}
              {beside.held ? (
                <span
                  className="ml-auto inline-flex flex-none items-center gap-1 rounded border border-hl/40 px-1.5 py-px text-label text-hl"
                  data-testid="side-pane-state"
                >
                  <Pin size={10} aria-hidden="true" />
                  {t('studio.side.pinned')}
                </span>
              ) : (
                <>
                  <span
                    className="ml-auto inline-flex flex-none items-center gap-1 rounded border border-line px-1.5 py-px text-label italic text-fg"
                    data-testid="side-pane-state"
                    title={t('studio.side.temporaryHint')}
                  >
                    <Clock size={10} aria-hidden="true" />
                    {t('studio.side.temporary')}
                  </span>
                  <IconButton
                    label={t('studio.workingSet.pin')}
                    hint={`${t('studio.workingSet.pin')} — ${t('studio.side.temporaryHint')}`}
                    icon={<Pin size={14} aria-hidden="true" />}
                    data-testid="side-pane-pin"
                    onClick={(): void => onPin(beside.ref)}
                  />
                </>
              )}
              <IconButton
                label={t(beside.held ? 'studio.workingSet.unpin' : 'studio.side.close')}
                icon={<X size={14} aria-hidden="true" />}
                data-testid="unpin-pane"
                onClick={(): void => onPin(undefined)}
              />
            </div>
            {sideBar?.(beside.ref)}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="side-pane-body">
              {renderEntity(beside.ref, 'pane', (): void => {})}
            </div>
          </section>
        ) : null}

        {focusRef !== undefined && focusLevel === 'full' ? (
          // The full view keeps a narrow, visible way back: the hidden object's
          // type and name and «Back to two panes» — or «Open beside» when there
          // is no second object. One real button: Tab / Enter / Space.
          <button
            type="button"
            className="flex w-12 flex-none flex-col items-center gap-2 rounded-md border border-line bg-panel-2 px-2.5 py-2 text-fg hover:border-hl/50 hover:bg-panel-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hl/70"
            data-testid="hidden-pane-strip"
            data-strip-action={beside !== undefined ? 'restore' : 'open-beside'}
            aria-label={
              beside !== undefined
                ? t('studio.full.restoreLabel').replace('{name}', named(besideIdentity, beside.ref.id))
                : t('studio.full.openBesideLabel')
            }
            title={
              beside !== undefined
                ? t('studio.full.restoreLabel').replace('{name}', named(besideIdentity, beside.ref.id))
                : t('studio.full.openBesideLabel')
            }
            onClick={(): void => {
              if (beside !== undefined) restoreTwoPanes();
              else onOpenBeside?.();
            }}
          >
            <Columns2 size={16} className="flex-none text-hl" aria-hidden="true" />
            {BesideIcon !== undefined ? <BesideIcon size={14} className="flex-none text-muted-foreground" aria-hidden="true" /> : null}
            {beside !== undefined && besideIdentity !== undefined ? (
              <span
                className="min-h-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-meta text-fg"
                style={{ writingMode: 'vertical-rl' }}
                data-testid="hidden-pane-name"
              >
                {besideIdentity.typeLabel} · {besideIdentity.label}
              </span>
            ) : (
              <span className="min-h-0 flex-1" />
            )}
            <span
              className="text-label font-semibold uppercase tracking-label flex-none text-hl"
              style={{ writingMode: 'vertical-rl' }}
            >
              {beside !== undefined ? t('studio.full.restore') : t('studio.full.openBeside')}
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default StudioWorkspace;
